/**
 * CreationStore + hooks — extracted from screens/events/creation/ (2026-09-02).
 *
 * The wizard this served was decommissioned 2026-08-24 and its step screens
 * deleted. What survived is the event CONFIG model and its readiness rules, which
 * the Journey Designer, the templates screen and the landing builder all read.
 * Moved up a directory so the retired `creation/` folder could be removed without
 * deleting live code.
 * — ESM port of the prototype's CreationStore.
 *
 * Pure event-emitter state store (no framework dependency). Screens call
 * CreationStore.setField(key, value) to record a wizard step's edits;
 * useCreationConfig() merges overrides on top of the type's base config.
 */

import { useEffect, useState } from "react";
import { CREATION_CONFIGS, REPEAT_LABELS_ALL, type CreationConfig } from "./event-config-types";
import { ServerErrors } from "./event-server-errors";
import { tzShortLabel } from "../../lib/datetime";
import { getLandingPage, getStudioEvent } from "@v0y4ge/api-client";
import { getActiveTenantSlug, onTenantSwitching } from "../../shell/access";
import { studioRequestOptions } from "../../lib/studio-config";
import type { AiEventDraft, AiMissingField } from "../ai/ai-api";
import { t as i18nT } from "../../lib/i18n";

export { REPEAT_LABELS_ALL };
export type { CreationConfig } from "./event-config-types";

// ── Types ───────────────────────────────────────────────────────────────────
export interface CreationOverrides {
  title?: string; description?: string; label?: string;
  start?: string; end?: string; allDay?: boolean;
  repeatIndex?: number; repeatEvery?: string; repeatEnds?: string;
  endsMode?: "never" | "on" | "after"; endsCount?: number; endsDate?: string;
  weekdays?: string[] | null; customInstances?: string[]; dayHours?: Record<string, unknown>;
  schedule?: string[];
  capacity?: number | null; waitlist?: number | null;
  tickets?: unknown[]; ticketGroups?: unknown[]; questions?: unknown[];
  guestList?: unknown[];
  slug?: string; landingHeadline?: string; landingSubhead?: string;
  landingPages?: unknown[];
  heroImage?: string | null;
  publishRows?: Record<string, [string, string][]>;
  publishWarnings?: string[];
  quickCreate?: boolean;
  [key: string]: unknown;
}

export interface StoreState {
  typeId: string;
  heroImage: string | null;
  method: { inPerson: boolean; virtual: boolean } | null;
  visibility: string | null;
  overrides: CreationOverrides;
  _editingId?: string | null;
  _draftId?: string | null;
  _templateMode?: boolean;
  _templateId?: string | null;
  _templateMeta?: Record<string, unknown> | null;
  /** Set when the wizard was prefilled by Build with AI — drives the assumptions/
   *  missing banner on the Basics step. Cleared on reset / dismiss. */
  _aiAssist?: { prompt: string; assumptions: string[]; missing: AiMissingField[] } | null;
}

/** repeatKind (brief §4) → REPEAT_LABELS_ALL index (types.ts). */
const AI_REPEAT_KIND_TO_INDEX: Record<string, number> = {
  none: 0, weekly: 1, "month-date": 2, "month-weekday": 3, "quarter-date": 4, "quarter-weekday": 5, custom: 6,
};

// ── Store ────────────────────────────────────────────────────────────────────
// Fresh-store default is the NEUTRAL blank config — never a real template.
// (Was "finwell": every fresh wizard entry pre-claimed "Quarterly Financial
// Wellness Series" in the footer + summary rail before the user chose anything.)
const _initState = (): StoreState => ({
  typeId: "blank", heroImage: null, method: null, visibility: null, overrides: {},
});

/* In-progress wizard draft is persisted to localStorage so an event you're
 * midway through creating survives a reload / recompile / new session. It's
 * rehydrated on load; starting a fresh event (CreateEventType → reset) or
 * publishing overwrites it. This is interim demo persistence — at wiring time
 * the real create/update-draft ops replace it. */
const CREATION_LS_KEY_BASE = "cd:studio:creation-draft:v1";
const creationLsKey = () => `${CREATION_LS_KEY_BASE}:${getActiveTenantSlug() ?? "none"}`;
function _persistCreation(): void {
  try { if (typeof localStorage !== "undefined") localStorage.setItem(creationLsKey(), JSON.stringify(_state)); } catch { /* noop (quota / disabled) */ }
}
function _rehydrateCreation(): StoreState | null {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(creationLsKey()) : null;
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (s && typeof s === "object" && typeof s.typeId === "string" && s.overrides && typeof s.overrides === "object") return s as StoreState;
    return null;
  } catch { return null; }
}

let _state: StoreState = _rehydrateCreation() || _initState();
const _listeners = new Set<() => void>();
const _emit = () => { _persistCreation(); _listeners.forEach((l) => { try { l(); } catch { /* noop */ } }); };
// Tenant-switch handling (docket 2026-07-27, item 1): this in-progress wizard
// draft had NONE — an event you were mid-way through authoring for tenant A
// would appear pre-filled the instant you started "a new event" under tenant
// B. Purely local (localStorage), so there's no network race to gate on —
// the optimistic tenant identity is safe to key off immediately.
onTenantSwitching(() => {
  _state = _rehydrateCreation() || _initState();
  _listeners.forEach((l) => { try { l(); } catch { /* noop */ } });
});

export const CreationStore = {
  get: (): StoreState => _state,

  setType: (id: string) => {
    if (!CREATION_CONFIGS[id]) return;
    ServerErrors.set(null); // fresh session — stale write errors don't apply
    
    const c = CREATION_CONFIGS[id];
    _state = {
      typeId: id, heroImage: _state.heroImage,
      method: { ...c.method }, visibility: c.visibility,
      overrides: { start: "", end: "", allDay: false, repeatIndex: 0, repeatEvery: "", repeatEnds: "", endsMode: "never", endsCount: 1, endsDate: "", weekdays: null, customInstances: [], dayHours: {}, schedule: [] },
      ...(_state._templateMode ? { _templateMode: true, _templateId: _state._templateId, _templateMeta: _state._templateMeta } : {}),
    };
    _emit();
  },

  setBlank: () => {
    ServerErrors.set(null);
    const c = CREATION_CONFIGS.blank;
    _state = { typeId: "blank", heroImage: null, method: { ...c.method }, visibility: c.visibility, overrides: {},
      ...(_state._templateMode ? { _templateMode: true, _templateId: _state._templateId, _templateMeta: _state._templateMeta } : {}) };
    _emit();
  },

  /** Full reset to a brand-new-event session — clears any prior edit/draft
   * (`_editingId`, `_draftId`), overrides, hero and template context. Called
   * when the wizard is entered fresh (not editing an existing event). */
  reset: () => { _state = _initState(); ServerErrors.set(null); _emit(); },

  /** Begin a brand-new Event Template session. Flips the wizard into template
   * mode (`_templateMode`) so it drops the occurrence-only steps and the header
   * reads "Create Event Template". Starts on a sensible base type; the type
   * picker lets the author swap it or build from scratch. */
  startTemplate: () => {
    ServerErrors.set(null);
    const typeId = CREATION_CONFIGS.vip ? "vip" : "blank";
    const c = CREATION_CONFIGS[typeId] || CREATION_CONFIGS.blank;
    _state = {
      typeId, heroImage: null,
      method: { ...c.method }, visibility: c.visibility, overrides: {},
      _templateMode: true, _templateId: null, _templateMeta: null,
    };
    _emit();
  },

  /** Seed a NEW event from a template's full-definition snapshot (everything
   *  the source event carried except dates/times + the registration window).
   *  Reuses the loadEvent raw→wizard mapping, then clears the edit linkage so
   *  it's a new event; start/end stay empty for the user to set. */
  applyTemplate: (t: { id: string; name?: string; type?: string; tags?: string[]; config?: Record<string, unknown> | null }) => {
    const snap = (t.config && typeof t.config === "object" ? t.config : {}) as Record<string, unknown>;
    CreationStore.loadEvent({ id: `tmpl-${t.id}`, title: (snap.title as string) || "", template: t.type || "", tags: t.tags || [], raw: snap });
    _state = { ..._state, _editingId: null };
    _emit();
  },

  /** Open an existing Event Template for editing — prefills the wizard from the
   * template's saved defaults and marks it template mode with its id, so the
   * header reads "Edit Event Template" and saving updates the same template. */
  loadTemplateForEdit: (t: {
    id: string; typeId?: string; name?: string; type?: string; desc?: string;
    tags?: string[]; hue?: string; status?: string; defaults?: { capacity?: number };
    config?: Record<string, unknown>;
  }) => {
    ServerErrors.set(null);
    if (t.config && Object.keys(t.config).length) {
      // Full snapshot available — hydrate the entire definition, then flip into
      // template mode (title shows the template's name).
      CreationStore.applyTemplate(t);
      _state = {
        ..._state,
        overrides: { ..._state.overrides, title: t.name || (_state.overrides.title as string) || "" },
        _templateMode: true, _templateId: t.id,
        _templateMeta: { status: t.status || "published", hue: t.hue || null },
      };
      _emit();
      return;
    }
    const typeId = (t.typeId && CREATION_CONFIGS[t.typeId]) ? t.typeId : "blank";
    const base = CREATION_CONFIGS[typeId] || CREATION_CONFIGS.blank;
    _state = {
      typeId, heroImage: null,
      method: { ...(base.method || { inPerson: true, virtual: false }) },
      visibility: base.visibility || "public",
      overrides: {
        title: t.name || "",
        label: t.type || base.label,
        description: t.desc || "",
        tags: Array.isArray(t.tags) ? t.tags : [],
        capacity: t.defaults?.capacity ?? null,
      },
      _templateMode: true, _templateId: t.id,
      _templateMeta: { status: t.status || "published", hue: t.hue || null },
    };
    _emit();
  },

  setHeroImage: (url: string) => { _state = { ..._state, heroImage: url }; _emit(); },
  clearHero:    () => { _state = { ..._state, heroImage: null }; _emit(); },

  setMethod: (m: { inPerson: boolean; virtual: boolean }) => {
    const safe = (m.inPerson || m.virtual) ? m : { inPerson: true, virtual: false };
    _state = { ..._state, method: safe };
    ServerErrors.clearForStoreKey("method");
    _emit();
  },

  setVisibility: (v: string) => { _state = { ..._state, visibility: v }; ServerErrors.clearForStoreKey("visibility"); _emit(); },

  endEditing: () => {
    if (_state._editingId) { _state = { ..._state, _editingId: null }; _emit(); }
  },

  setField: (key: string, value: unknown) => {
    _state = { ..._state, overrides: { ..._state.overrides, [key]: value } };
    // Editing a field clears its inline server error (2026-07-12 docket §2) —
    // the user is fixing it; the next write re-validates server-side.
    ServerErrors.clearForStoreKey(key);
    _emit();
  },

  setDraftId: (id: string) => { _state = { ..._state, _draftId: id }; _emit(); },

  loadEvent: (ev: Record<string, unknown>) => {
    if (!ev) return;
    ServerErrors.set(null);
    // 2026-07-13 docket §1a (#795/#794/#793) + #792 — the raw StudioEventDto the
    // backend persists + returns is the SINGLE authoritative hydration source.
    // The old `ev._creation` primary branch was DEAD (`_creation`/creationSnapshot
    // is template-only — the events DTO never returns it), so edit-open always
    // fell through to a title/date/capacity-only rebuild and reset every other
    // field. Deleted; everything below reads `ev.raw` with the flattened display
    // row as the fallback (rows minted client-side carry a minimal raw).
    let typeId = "blank";
    for (const k of Object.keys(CREATION_CONFIGS)) {
      const c = CREATION_CONFIGS[k];
      if (c?.label && ev.template && c.label.toLowerCase() === String(ev.template).toLowerCase()) { typeId = k; break; }
    }
    const base = CREATION_CONFIGS[typeId] || CREATION_CONFIGS.blank;
    const raw = (ev.raw && typeof ev.raw === "object" ? ev.raw : {}) as Record<string, unknown>;
    const s = (v: unknown): string => (v == null ? "" : String(v));

    // Flattened-row fallbacks ("Fri 8 May 2026" + "10:00 — 12:00").
    const [t0, t1] = String(ev.time || "").split(" — ");
    const date = (ev.date as string) || "";
    const fallbackStart = t0 ? `${date} · ${t0}` : date;
    const fallbackEnd = t1 ? `${date} · ${t1}` : date;

    // #795 — method from raw.format. Visibility stays base-derived on purpose:
    // the wizard uses a richer vocabulary than the low-level enum.
    const format = s(raw.format);
    const method = format === "hybrid" ? { inPerson: true, virtual: true }
      : format === "virtual" ? { inPerson: false, virtual: true }
      : format === "in_person" ? { inPerson: true, virtual: false }
      : { ...(base.method || { inPerson: true, virtual: false }) };

    // #794 — the in-person Details step binds venueName / address / locationId /
    // geo (NOT locationAddress); locationMode is derived by InPersonAddress, so
    // it's deliberately omitted. geo restores only from a VALIDATED structured
    // EventLocation — never fake a validated flag.
    const venueName = s(raw.locationName ?? raw.location_name);
    const address = s(raw.locationAddress ?? raw.location_address);
    const locationId = s(raw.locationId ?? raw.location_id);
    const structured = raw.location as { address?: Record<string, unknown>; validated?: boolean; lat?: number; lng?: number } | null | undefined;
    const geo = structured && structured.validated
      ? { placeId: "", address: structured.address ?? null, lat: structured.lat, lng: structured.lng, tz: s(raw.timezone) || undefined, validated: true }
      : null;
    const locationSummary = venueName || address || (format === "virtual" ? "Online" : "");

    // Timezone: the DTO carries bare IANA; the wizard's TimeZonePicker value
    // shape is "(GMT-5) America/New_York" (CreateEventDates derives the IANA
    // via split(" ").pop(), which also accepts a bare zone).
    const iana = s(raw.timezone);
    const off = iana ? tzShortLabel(iana) : "";
    const tzValue = iana ? (off ? `(${off}) ${iana}` : iana) : "";

    // #793 — FULL ticket selection: per-type qty/kind/price configs + groups,
    // not just the checked type ids (ids alone reset every quantity to 0).
    const ticketTypeIds = Array.isArray(raw.ticketTypeIds) ? (raw.ticketTypeIds as string[])
      : Array.isArray(ev.ticketTypeIds) ? (ev.ticketTypeIds as string[]) : [];
    const ticketTypeConfigs = (raw.ticketTypeConfigs && typeof raw.ticketTypeConfigs === "object") ? raw.ticketTypeConfigs : undefined;
    const ticketGroupIds = Array.isArray(raw.ticketGroupIds) ? (raw.ticketGroupIds as string[]) : undefined;

    // #816 round-trip — rebuild the Registration step's stdFields from the DTO's
    // registrationQuestions so edit-open keeps the saved required/optional/off
    // modes (fieldKey for the four defaults, the `std-<id>` convention for the
    // rest; an absent non-default question means it was configured 'off').
    // Events saved before the wizard emitted the array (empty regQs) hydrate
    // nothing, so the step keeps its own defaults.
    const regQs = Array.isArray(raw.registrationQuestions) ? (raw.registrationQuestions as { id?: string; fieldKey?: string; required?: boolean }[]) : [];
    const stdFields = stdFieldsFromRegQs(regQs);

    // #792 — landing state: hydrate whatever the DTO returns (nested `landing`
    // object or flat slug/landingDesign members). If the DTO carries neither,
    // there is nothing to restore client-side — declared in the changelog.
    const landing = (raw.landing && typeof raw.landing === "object" ? raw.landing : {}) as Record<string, unknown>;
    const slug = s(landing.slug ?? raw.slug);
    const landingDesign = (landing.landingDesign ?? raw.landingDesign) as Record<string, unknown> | undefined;
    const landingHeadline = s(landing.landingHeadline ?? raw.landingHeadline);
    const landingSubhead = s(landing.landingSubhead ?? raw.landingSubhead);
    const useLanding = (landing.useLanding ?? raw.useLanding) as boolean | undefined;

    const overrides = {
      title: s(ev.title ?? raw.title),
      label: (ev.template as string) || base.label,
      description: s(raw.description ?? ev.description),  // #795 — create.basics reads cfg.description
      tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : (Array.isArray(ev.tags) ? (ev.tags as string[]) : []),
      start: s(raw.startsAt) || fallbackStart,
      end: s(raw.endsAt) || fallbackEnd,
      capacity: (raw.capacity as number) ?? (ev.capacity as number),
      waitlist: (raw.waitlistCapacity as number) ?? (ev.waitlist as number) ?? 0,
      ...(tzValue ? { timezone: tzValue } : {}),
      ...(venueName ? { venueName } : {}),
      ...(address ? { address, locationAddress: address } : {}),
      ...(locationId ? { locationId } : {}),
      ...(geo ? { geo } : {}),
      ...(locationSummary ? { location: locationSummary } : {}),
      ...(s(raw.meetingLink ?? raw.meeting_link) ? { virtualLink: s(raw.meetingLink ?? raw.meeting_link) } : {}),
      ...(ticketTypeIds.length ? { ticketTypeIds } : {}),
      ...(ticketTypeConfigs ? { ticketTypeConfigs } : {}),
      ...(ticketGroupIds && ticketGroupIds.length ? { ticketGroupIds } : {}),
      ...(stdFields ? { stdFields } : {}),
      ...(slug ? { slug } : {}),
      ...(landingDesign ? { landingDesign } : {}),
      ...(landingHeadline ? { landingHeadline } : {}),
      ...(landingSubhead ? { landingSubhead } : {}),
      ...(useLanding !== undefined ? { useLanding } : {}),
    } as unknown as CreationOverrides;

    _state = {
      typeId,
      heroImage: s(raw.imageUrl) || (ev.heroImage as string) || null,  // #795
      method,
      visibility: base.visibility || "public",
      overrides,
      _editingId: ev.id as string,
    };
    _emit();

    // #792 (docket 2026-07-13 §2) — the list/edit DTO does NOT inline the saved
    // landing design (blocks live in landing_pages.blocks_json), so the sync
    // hydration above restores it only in the rare case `raw` carried it. Fetch
    // the saved page the same way the standalone editor does and patch the
    // wizard's landing overrides in when it resolves — so reopening a published
    // event for edit keeps its landing-page design instead of resetting to the
    // default blocks. Fire-and-forget: loadEvent stays synchronous.
    void hydrateLandingDesign(String(ev.id));
    // List rows carry a MINIMAL raw (no registrationQuestions — only the full
    // getEvent DTO inlines them), so direct entry to the landing step rendered
    // the fallback capture form until the Registration step was opened by hand
    // (operator report, 2026-07-14). Same pattern as landing design: fetch the
    // full DTO and patch stdFields in when it resolves.
    if (!stdFields) void hydrateRegistrationConfig(String(ev.id));
  },

  /**
   * Prefill a fresh wizard from an AI-generated draft (aiAssist → CreationEventInput,
   * brief §3). Maps the contract draft onto the wizard's config overrides — the
   * same seam loadEvent() uses for a stored DTO — then stashes the assumptions/
   * missing so the Basics step can surface them. Nothing persists until the user
   * reviews and saves through the normal Publish path (no-fake-interactivity).
   */
  prefillFromAiDraft: (draft: AiEventDraft, meta: { prompt: string; assumptions?: string[]; missing?: AiMissingField[] }) => {
    ServerErrors.set(null);
    const base = CREATION_CONFIGS.blank;
    const s = (v: unknown): string => (v == null ? "" : String(v));

    const fmt = draft.format;
    const method = fmt === "hybrid" ? { inPerson: true, virtual: true }
      : fmt === "virtual" ? { inPerson: false, virtual: true }
      : { inPerson: true, virtual: false };

    const vis = draft.visibility
      ? (/priv|invite|rsvp/i.test(String(draft.visibility)) ? "Invite · RSVP" : "Public · Anyone with link")
      : base.visibility;

    const iana = s(draft.timezone);
    const off = iana ? tzShortLabel(iana) : "";
    const tzValue = iana ? (off ? `(${off}) ${iana}` : iana) : "";

    const rc = (draft.repeatConfig || {}) as Record<string, unknown>;
    const endsModeRaw = s(rc.endsMode) || "never";
    const endsMode: "never" | "on" | "after" = endsModeRaw === "count" ? "after" : endsModeRaw === "date" ? "on" : "never";

    const overrides: CreationOverrides = {
      title: s(draft.title),
      description: s(draft.description),
      label: s(draft.category) || s(draft.type) || base.label,
      tags: Array.isArray(draft.tags) ? draft.tags : [],
      start: s(draft.startsAt),
      end: s(draft.endsAt),
      capacity: draft.capacity ?? null,
      waitlist: draft.waitlistCapacity ?? (draft.allowWaitlist ? 0 : null),
      repeatIndex: AI_REPEAT_KIND_TO_INDEX[s(draft.repeatKind)] ?? 0,
      endsMode,
      ...(rc.endsCount != null ? { endsCount: Number(rc.endsCount) } : {}),
      ...(rc.endsDate ? { endsDate: s(rc.endsDate) } : {}),
      ...(tzValue ? { timezone: tzValue } : {}),
      ...(s(draft.locationId) ? { locationId: s(draft.locationId) } : {}),
      ...(s(draft.locationAddress) ? { address: s(draft.locationAddress), locationAddress: s(draft.locationAddress), location: s(draft.locationAddress) } : {}),
      ...(s(draft.meetingLink) ? { virtualLink: s(draft.meetingLink) } : {}),
      ...(Array.isArray(draft.ticketTypeIds) && draft.ticketTypeIds.length ? { ticketTypeIds: draft.ticketTypeIds } : {}),
      ...(draft.ticketTypeConfigs ? { ticketTypeConfigs: draft.ticketTypeConfigs } : {}),
    };

    _state = {
      typeId: "blank",
      heroImage: s(draft.imageUrl) || null,
      method,
      visibility: vis,
      overrides,
      _aiAssist: { prompt: meta.prompt, assumptions: meta.assumptions || [], missing: meta.missing || [] },
    };
    _emit();
  },

  /** Dismiss the AI assumptions/missing banner (keeps the prefilled fields). */
  clearAiAssist: () => { if (_state._aiAssist) { _state = { ..._state, _aiAssist: null }; _emit(); } },

  subscribe: (l: () => void) => {
    _listeners.add(l);
    return () => _listeners.delete(l);
  },

  set: function(id: string) { this.setType(id); },
};

/**
 * #792 — reopening an event for edit must restore its SAVED landing-page design.
 * The events DTO doesn't inline the design, so mirror the standalone editor:
 * fetch the page by event id (getLandingPage) and hydrate the wizard's landing
 * overrides (blocks / theme / font / slug / headline / subhead) when it
 * resolves. Guards against a late resolve landing on a DIFFERENT session (the
 * user may have started a new event by then) by re-checking `_editingId`, and
 * never fabricates content — a 404 / no-blocks response leaves the wizard's
 * defaults untouched (so a genuinely-new page still starts from defaults).
 */
async function hydrateLandingDesign(eventId: string): Promise<void> {
  if (!eventId || typeof getLandingPage !== "function") return;
  try {
    const r = await getLandingPage(eventId, studioRequestOptions("landing-pages")) as Record<string, any>;
    if (_state._editingId !== eventId) return; // session moved on before the fetch resolved
    const blocks = Array.isArray(r?.blocks) ? r.blocks : null;
    if (!blocks || !blocks.length) return;      // nothing saved server-side — keep defaults
    const theme = r?.themeConfig?.id ?? r?.themeConfig?.name ?? r?.theme;
    const font = r?.font;
    CreationStore.setField("landingDesign", { blocks, ...(theme ? { theme: String(theme) } : {}), ...(font ? { font: String(font) } : {}) });
    if (r?.slug) CreationStore.setField("slug", String(r.slug));
    if (r?.landingHeadline) CreationStore.setField("landingHeadline", String(r.landingHeadline));
    if (r?.landingSubhead) CreationStore.setField("landingSubhead", String(r.landingSubhead));
  } catch (e) {
    console.warn("getLandingPage (edit-open hydration) failed — keeping landing defaults:", e);
  }
}

/** #816 rebuild — registrationQuestions (contract array) → the Registration
 *  step's stdFields mode map. Shared by sync loadEvent (raw DTO) and the async
 *  getEvent hydrator below. Empty regQs ⇒ undefined (nothing saved — keep
 *  step defaults, never fabricate modes). */
function stdFieldsFromRegQs(regQs: { id?: string; fieldKey?: string; required?: boolean }[]): Record<string, string> | undefined {
  if (!regQs.length) return undefined;
  const m: Record<string, string> = { firstName: "required", lastName: "required", email: "required" }; // ui-config — field-key→mode map (locked always-required fields), not identity/seed data
  const phone = regQs.find((q) => q.fieldKey === "phone" || q.id === "default-phone");
  m.phone = phone ? (phone.required ? "required" : "optional") : "optional";
  for (const k of ["homeAddress", "company", "title"]) {
    const q = regQs.find((x) => x.id === `std-${k}`);
    m[k] = q ? (q.required ? "required" : "optional") : "off";
  }
  return m;
}

/**
 * Registration-config hydration for edit-open (operator report, 2026-07-14).
 * List rows pass a minimal raw to loadEvent — registrationQuestions only ride
 * the full getEvent DTO — so entering the landing step directly showed the
 * fallback capture form until the Registration step was visited by hand.
 * Mirrors hydrateLandingDesign: fire-and-forget fetch, `_editingId` staleness
 * guard, and it yields to any stdFields the user has produced meanwhile (the
 * Registration step mounted while the fetch was in flight) — never overwrites.
 */
async function hydrateRegistrationConfig(eventId: string): Promise<void> {
  if (!eventId || typeof getStudioEvent !== "function") return;
  try {
    const r = await getStudioEvent(eventId, studioRequestOptions("events")) as Record<string, any>;
    if (_state._editingId !== eventId) return; // session moved on before the fetch resolved
    if ((_state.overrides as Record<string, unknown>).stdFields) return; // user got there first
    const dto = (r?.event && typeof r.event === "object" ? r.event : r) as Record<string, unknown>;
    const regQs = Array.isArray(dto?.registrationQuestions) ? (dto.registrationQuestions as { id?: string; fieldKey?: string; required?: boolean }[]) : [];
    const stdFields = stdFieldsFromRegQs(regQs);
    if (stdFields) CreationStore.setField("stdFields", stdFields);
  } catch (e) {
    console.warn("getStudioEvent (registration-config hydration) failed — keeping step defaults:", e);
  }
}

// ── Hooks ────────────────────────────────────────────────────────────────────
export function useCreationState(): StoreState {
  const [, force] = useState(0);
  useEffect(() => {
    const unsub = CreationStore.subscribe(() => force((n) => n + 1));
    return () => { unsub(); };
  }, []);
  return CreationStore.get();
}

export function useCreationConfig(): CreationConfig & CreationOverrides {
  const state = useCreationState();
  const base = CREATION_CONFIGS[state.typeId] || CREATION_CONFIGS.blank;
  return {
    ...base,
    ...state.overrides,
    heroImage: state.heroImage,
    method: state.method || base.method,
    visibility: state.visibility || base.visibility,
  } as CreationConfig & CreationOverrides;
}

// ── Wizard routing helpers ───────────────────────────────────────────────────
export function isPublicAnyone(visibility?: string): boolean {
  const v = (visibility || "").trim();
  return v.startsWith("Public ·") && /anyone/i.test(v);
}

// ── Required-field validation (single source of truth) ──────────────────────
export interface RequiredCheck { label: string; step: string; done: boolean }
/** Every hard requirement to create/publish an event, with the step that owns
 * it. Used to mark fields as required in-wizard and to surface what's missing on
 * the Review & publish step. */
export function requiredEventChecks(cfg: Partial<CreationConfig> & Record<string, unknown>, opts?: { template?: boolean }): RequiredCheck[] {
  const has = (v: unknown) => v != null && String(v).trim() !== "";
  const m = (cfg.method as { inPerson?: boolean; virtual?: boolean } | null) || {};
  const typeIds = (cfg.ticketTypeIds as unknown[] | undefined) || [];
  const groupIds = (cfg.ticketGroupIds as unknown[] | undefined) || [];
  const tickets = (cfg.tickets as unknown[] | undefined) || [];
  const checks: RequiredCheck[] = [
    { label: i18nT("events.creationStore.eventTitle", "Event title"), step: "basics", done: has(cfg.title) },
    { label: i18nT("events.creationStore.description", "Description"), step: "basics", done: has(cfg.description) },
    { label: i18nT("events.creationStore.format", "Format"), step: "details", done: !!(m.inPerson || m.virtual) },
    { label: i18nT("events.creationStore.visibility", "Visibility"), step: "details", done: has(cfg.visibility) },
  ];
  // Location is format-dependent: physical venue for in-person/hybrid, meeting
  // provider for virtual/hybrid (mirrors CreateEventDetails' Continue gate).
  if (m.inPerson) checks.push({ label: i18nT("events.creationStore.location", "Location"), step: "details", done: has(cfg.location) || has(cfg.locationId) || has(cfg.venueName) || has(cfg.address) });
  if (m.virtual) checks.push({ label: i18nT("events.creationStore.meetingProvider", "Meeting provider"), step: "details", done: has(cfg.virtualProvider) });
  if (!opts?.template) {
    checks.push({ label: i18nT("events.creationStore.startDateTime", "Start date & time"), step: "schedule", done: has(cfg.start) });
    checks.push({ label: i18nT("events.creationStore.endDateTime", "End date & time"), step: "schedule", done: has(cfg.end) });
  }
  checks.push({ label: i18nT("events.creationStore.atLeastOneTicketTypeOrGroup", "At least one ticket type or group"), step: "tickets", done: (typeIds.length + groupIds.length) > 0 || tickets.length > 0 });
  return checks;
}

/* wizardFlowRoutes / wizardNeighbors were REMOVED 2026-08-24 with the event
 * creation wizard (operator: "sweep the wizard now"). They computed step-to-step
 * navigation across the `create.*` routes, which no longer exist.
 *
 * WHAT THIS MODULE IS NOW: the event DRAFT + field model, not wizard state. Its
 * folder name is historical — the wizard's twelve step screens, its shell and
 * its form utilities are deleted. Three kept surfaces read what remains:
 *   • the Journey designer — requiredEventChecks (readiness rows), isPublicAnyone
 *   • the events list      — CreationStore draft state on duplicate
 *   • the landing builder  — useCreationConfig + CreationStore.setField
 *
 * The `route` field on RequiredCheck still carries `create.*` strings. They are
 * KEYS now, not routes: the designer maps them onto its own Setup tabs
 * (ROUTE_TO_SETUP in NewJourneys). Nothing navigates to them.
 */
export const LEGACY_VIRTUAL_PROVIDER_IDS: Record<string, string> = { teams: "msteams", meet: "gmeet", youtube: "custom" };
export function normalizeVirtualProvider(v: unknown): string {
  const s = typeof v === "string" ? v.trim() : "";
  return s ? (LEGACY_VIRTUAL_PROVIDER_IDS[s] || s) : "";
}

/**
 * Is this event currently live? Single source of truth for the three wizard
 * surfaces that used to compute it separately (the deleted wizard shell + Live step,
 * CreateEventPublish) — kept in one place so "live" can't mean something
 * slightly different on one screen than another.
 *
 * 2026-07-31 (operator): the wizard no longer LOCKS editing on a live event —
 * a manager can change any step while it's running. This only answers "is it
 * live", for the still-legitimate uses: the informational banner, and gating
 * the "you're changing a live event" confirm on Publish.
 */
export function deriveEventIsLive(
  cfg: Partial<CreationConfig> & Record<string, unknown>,
  storedEvent: { status?: string; raw?: unknown } | null | undefined,
): boolean {
  const publishedFlag = !!(cfg as { _published?: boolean })._published;
  const storedRaw = (storedEvent?.raw ?? null) as { isLocked?: boolean } | null;
  const storedLive = storedRaw && typeof storedRaw.isLocked === "boolean"
    ? storedRaw.isLocked
    : !!storedEvent && (storedEvent.status === "live" || storedEvent.status === "upcoming");
  const takenOffline = !publishedFlag && !!(cfg as { _wasLive?: boolean })._wasLive;
  return publishedFlag || (storedLive && !takenOffline);
}
